import React, { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LoginForm from "./LoginForm";
import RegisterForm from "./RegisterForm";
import { ArrowLeft, LogIn, UserPlus } from "lucide-react";

type Tab = "login" | "register";

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

export default function AuthPage() {
  const { i18n } = useTranslation();
  const [params, setParams] = useSearchParams();

  const isVN = i18n.language?.startsWith("vi");

  const text = {
    back: isVN ? "Quay lại" : "Back",
    brand: "OWL DREAM",
    login: isVN ? "Đăng nhập" : "Login",
    register: isVN ? "Đăng ký" : "Register",
    loginKicker: isVN ? "Member access" : "Member access",
    registerKicker: isVN ? "Create account" : "Create account",
    loginDesc: isVN ? "Đăng nhập để tiếp tục." : "Sign in to continue.",
    registerDesc: isVN ? "Tạo tài khoản để bắt đầu." : "Create your account.",
    footer: isVN ? "Minimal / clean / no scroll" : "Minimal / clean / no scroll",
  };

  const tab: Tab = useMemo(() => {
    const q = (params.get("tab") || "").toLowerCase();
    return q === "register" ? "register" : "login";
  }, [params]);

  const setTab = (next: Tab) => {
    const nextParams = new URLSearchParams(params);
    nextParams.set("tab", next);
    setParams(nextParams, { replace: true });
  };

  return (
    <main className="h-screen overflow-hidden bg-[#f6efe4] text-brand-brown">
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(189,160,103,0.10),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(57,34,25,0.06),transparent_30%)]" />
      </div>

      <div className="relative flex h-full flex-col">
        {/* top bar */}
        <header className="border-b border-brand-brown/10 bg-[#f6efe4]/82 backdrop-blur-xl">
          <div className="mx-auto flex h-[64px] w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-10">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.22em] text-brand-brown/58 transition hover:text-brand-brown"
            >
              <ArrowLeft className="h-4 w-4" />
              {text.back}
            </Link>

            <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-brand-brown/38">
              {text.brand}
            </p>
          </div>
        </header>

        {/* centered auth */}
        <section className="flex min-h-0 flex-1 items-center justify-center px-4 py-4 sm:px-6 sm:py-5">
          <div className="w-full max-w-[560px] lg:max-w-[620px]">
            <div className="overflow-hidden rounded-[28px] border border-brand-brown/10 bg-white/72 shadow-[0_24px_70px_rgba(43,31,26,0.08)] backdrop-blur-xl">
              <div className="border-b border-brand-brown/8 px-5 py-4 sm:px-6 sm:py-5">
                <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-brand-brown/40">
                  {tab === "login" ? text.loginKicker : text.registerKicker}
                </p>

                <div className="mt-3 flex items-end justify-between gap-4">
                  <div>
                    <h1 className="text-[24px] font-medium tracking-[0.01em] text-brand-brown sm:text-[28px]">
                      {tab === "login" ? text.login : text.register}
                    </h1>
                    <p className="mt-1 text-sm text-brand-brown/56">
                      {tab === "login" ? text.loginDesc : text.registerDesc}
                    </p>
                  </div>

                  <div className="hidden h-10 w-10 items-center justify-center rounded-full border border-brand-brown/10 bg-brand-ivory/72 text-brand-brown/58 sm:flex">
                    {tab === "login" ? (
                      <LogIn className="h-4 w-4" />
                    ) : (
                      <UserPlus className="h-4 w-4" />
                    )}
                  </div>
                </div>
              </div>

              <div className="p-3 sm:p-4">
                <div className="rounded-[22px] border border-brand-brown/8 bg-brand-ivory/78 p-3 sm:p-4">
                  <div className="mb-4 grid grid-cols-2 gap-2 rounded-[18px] border border-brand-brown/10 bg-white/62 p-1">
                    <button
                      type="button"
                      onClick={() => setTab("login")}
                      className={cn(
                        "inline-flex h-10 items-center justify-center gap-2 rounded-[14px] px-4 text-[10px] font-medium uppercase tracking-[0.2em] transition",
                        tab === "login"
                          ? "bg-brand-brown text-brand-ivory shadow-[0_10px_20px_rgba(43,31,26,0.14)]"
                          : "text-brand-brown/55 hover:bg-brand-brown/5 hover:text-brand-brown",
                      )}
                    >
                      <LogIn className="h-4 w-4" />
                      {text.login}
                    </button>

                    <button
                      type="button"
                      onClick={() => setTab("register")}
                      className={cn(
                        "inline-flex h-10 items-center justify-center gap-2 rounded-[14px] px-4 text-[10px] font-medium uppercase tracking-[0.2em] transition",
                        tab === "register"
                          ? "bg-brand-brown text-brand-ivory shadow-[0_10px_20px_rgba(43,31,26,0.14)]"
                          : "text-brand-brown/55 hover:bg-brand-brown/5 hover:text-brand-brown",
                      )}
                    >
                      <UserPlus className="h-4 w-4" />
                      {text.register}
                    </button>
                  </div>

                  <div className="min-h-0">
                    {tab === "login" ? <LoginForm /> : <RegisterForm />}
                  </div>
                </div>
              </div>

              <div className="border-t border-brand-brown/8 px-5 py-3 sm:px-6">
                <p className="text-center text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/35">
                  {text.footer}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}